/** CAN/ULC-S536 Annex C2 — simplified battery capacity (Ah) from measured loads. */

export type AlarmDurationKey = '5min' | '30min' | '1h' | '2h';

export const ALARM_DURATION_HOURS: Record<AlarmDurationKey, number> = {
    '5min': 5 / 60,
    '30min': 0.5,
    '1h': 1,
    '2h': 2
};

export type BatteryLoadInputs = {
    supervisoryCurrentMa: number;
    alarmCurrentA: number;
    alarmDuration: AlarmDurationKey;
    deratingFactor: number;
    installedBatteryAh: number;
    installedBatteryQty: number;
    ratedVoltage: number;
    voltageAfterTest?: number;
};

export type BatteryLoadResult = {
    standbyAh: number;
    alarmAh: number;
    subtotalAh: number;
    requiredAh: number;
    installedTotalAh: number;
    ratingMeetsLoad: boolean | null;
    voltage85Ok: boolean | null;
    summaryNote: string;
};

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

export function computeBatteryLoad(inputs: BatteryLoadInputs): BatteryLoadResult {
    const standbyAh = round2((inputs.supervisoryCurrentMa / 1000) * 24);
    const alarmHours = ALARM_DURATION_HOURS[inputs.alarmDuration];
    const alarmAh = round2(inputs.alarmCurrentA * alarmHours);
    const subtotalAh = round2(standbyAh + alarmAh);
    const factor = inputs.deratingFactor > 0 ? inputs.deratingFactor : 1.25;
    const requiredAh = round2(subtotalAh * factor);
    const installedTotalAh = round2(inputs.installedBatteryAh * Math.max(1, inputs.installedBatteryQty));

    const ratingMeetsLoad =
        inputs.installedBatteryAh > 0
            ? installedTotalAh >= requiredAh
            : null;

    const minVoltage = inputs.ratedVoltage > 0 ? inputs.ratedVoltage * 0.85 : null;
    const voltage85Ok =
        minVoltage != null && inputs.voltageAfterTest != null && inputs.voltageAfterTest > 0
            ? inputs.voltageAfterTest >= minVoltage
            : null;

    const summaryNote = [
        `Standby: ${inputs.supervisoryCurrentMa} mA × 24 h = ${standbyAh} Ah`,
        `Alarm: ${inputs.alarmCurrentA} A × ${alarmHours} h = ${alarmAh} Ah`,
        `Subtotal: ${subtotalAh} Ah × ${factor} derating = ${requiredAh} Ah required`,
        inputs.installedBatteryAh > 0
            ? `Installed: ${inputs.installedBatteryQty}× ${inputs.installedBatteryAh} Ah = ${installedTotalAh} Ah`
            : null,
        ratingMeetsLoad != null ? (ratingMeetsLoad ? 'Installed rating ≥ required.' : 'Installed rating BELOW required.') : null,
        voltage85Ok != null
            ? voltage85Ok
                ? `Post-test ${inputs.voltageAfterTest} V ≥ 85% of ${inputs.ratedVoltage} V.`
                : `Post-test ${inputs.voltageAfterTest} V below 85% of ${inputs.ratedVoltage} V.`
            : null
    ]
        .filter(Boolean)
        .join('\n');

    return {
        standbyAh,
        alarmAh,
        subtotalAh,
        requiredAh,
        installedTotalAh,
        ratingMeetsLoad,
        voltage85Ok,
        summaryNote
    };
}

export function isEmergencyPowerSection(category: string): boolean {
    return /22\.5\s*[—-]\s*Emergency Power Supply/i.test(category.trim());
}

export type PowerProvidedBy = 'batteries' | 'generator' | 'ups' | 'combination';
export type BatteryChemistry = 'sla' | 'nicad' | 'lithium' | 'wet_lead';

/** One emergency power supply / battery bank (PDF: complete section for each). */
export type BatteryUnitRecord = BatteryLoadInputs & {
    id: string;
    label: string;
    epsLocation?: string;
    epsIdentification?: string;
    batteryChemistry?: BatteryChemistry;
    manufacturerDateCode?: string;
    manufacturerMethodSpec?: string;
    voltageMainOn?: number;
    chargingCurrentMa?: number;
    voltageSupervisoryOff?: number;
    currentSupervisoryMa?: number;
    voltageAlarmOff?: number;
    currentAlarmA?: number;
    chargingCurrentA?: number;
    requiredAh?: number;
    summaryNote?: string;
};

export type BatteryLoadSheet = {
    powerProvidedBy?: PowerProvidedBy;
    /** Shared NBC alarm duration default for new units */
    alarmDuration?: AlarmDurationKey;
    units: BatteryUnitRecord[];
};

export function newBatteryUnitId(): string {
    return `eps_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyBatteryUnit(index: number, alarmDuration: AlarmDurationKey = '5min'): BatteryUnitRecord {
    return {
        id: newBatteryUnitId(),
        label: `Emergency power supply ${index}`,
        alarmDuration,
        deratingFactor: 1.25,
        installedBatteryAh: 0,
        installedBatteryQty: 2,
        ratedVoltage: 12,
        supervisoryCurrentMa: 0,
        alarmCurrentA: 0
    };
}

/** Accept legacy flat sheet or new multi-unit shape. */
export function normalizeBatteryLoadSheet(raw: unknown): BatteryLoadSheet {
    if (!raw || typeof raw !== 'object') {
        return { powerProvidedBy: 'batteries', alarmDuration: '5min', units: [emptyBatteryUnit(1)] };
    }
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.units) && obj.units.length > 0) {
        return {
            powerProvidedBy: (obj.powerProvidedBy as PowerProvidedBy) || 'batteries',
            alarmDuration: (obj.alarmDuration as AlarmDurationKey) || '5min',
            units: obj.units.map((u, i) => {
                const unit = u as BatteryUnitRecord;
                return {
                    ...emptyBatteryUnit(i + 1),
                    ...unit,
                    id: unit.id || newBatteryUnitId(),
                    label: unit.label || `Emergency power supply ${i + 1}`
                };
            })
        };
    }
    // Legacy single-unit flat object
    const legacy = obj as Record<string, unknown>;
    const unitFields = { ...legacy };
    delete unitFields.powerProvidedBy;
    delete unitFields.alarmDuration;
    delete unitFields.units;
    const alarmDuration = (legacy.alarmDuration as AlarmDurationKey) || '5min';
    return {
        powerProvidedBy: (legacy.powerProvidedBy as PowerProvidedBy) || 'batteries',
        alarmDuration,
        units: [
            {
                ...emptyBatteryUnit(1, alarmDuration),
                ...(unitFields as Partial<BatteryUnitRecord>),
                id: String(unitFields.id || newBatteryUnitId()),
                label: String(unitFields.label || unitFields.epsIdentification || 'Emergency power supply 1')
            }
        ]
    };
}

export function formatMultiUnitChecklistNotes(sheet: BatteryLoadSheet): {
    ga01: string;
    ga02: string;
    ga04: string;
    ga05: string;
    ga06: string;
    ga07: string;
    g03: string;
    g03c: string;
    g04: string;
    g04c: string;
    g05: string;
    g05c: string;
    g11: string;
    g12: string;
    g18: string;
    g22: string;
} {
    const blocks = sheet.units.map((unit, i) => {
        const n = sheet.units.length > 1 ? `[${i + 1}] ` : '';
        const head = unit.label || unit.epsIdentification || `Supply ${i + 1}`;
        const inputs: BatteryLoadInputs = {
            supervisoryCurrentMa: unit.supervisoryCurrentMa,
            alarmCurrentA: unit.alarmCurrentA,
            alarmDuration: unit.alarmDuration,
            deratingFactor: unit.deratingFactor,
            installedBatteryAh: unit.installedBatteryAh,
            installedBatteryQty: unit.installedBatteryQty,
            ratedVoltage: unit.ratedVoltage,
            voltageAfterTest: unit.voltageAfterTest
        };
        const calc =
            inputs.supervisoryCurrentMa > 0 && inputs.alarmCurrentA > 0
                ? computeBatteryLoad(inputs)
                : null;
        return { n, head, unit, calc };
    });

    const line = (pick: (b: (typeof blocks)[0]) => string | undefined) =>
        blocks
            .map((b) => {
                const v = pick(b);
                return v ? `${b.n}${b.head}: ${v}` : '';
            })
            .filter(Boolean)
            .join('\n');

    const g11 = blocks
        .map((b) => {
            const note = b.unit.summaryNote || b.calc?.summaryNote;
            const ah = b.unit.requiredAh ?? b.calc?.requiredAh;
            if (!note && ah == null) return '';
            const prefix = sheet.units.length > 1 ? `--- ${b.head} ---\n` : '';
            const ahLine = ah != null ? `Calculated: ${ah} Ah required.\n` : '';
            return `${prefix}${ahLine}${note || ''}`.trim();
        })
        .filter(Boolean)
        .join('\n\n');

    return {
        ga01: line((b) => b.unit.epsLocation),
        ga02: line((b) => b.unit.epsIdentification || b.head),
        ga04: line((b) => b.unit.batteryChemistry),
        ga05: line((b) =>
            b.unit.installedBatteryQty != null && b.unit.installedBatteryAh
                ? `Qty ${b.unit.installedBatteryQty}, ${b.unit.installedBatteryAh} Ah each`
                : undefined
        ),
        ga06: line((b) => (b.unit.ratedVoltage != null ? `${b.unit.ratedVoltage} V dc` : undefined)),
        ga07: line((b) => (b.unit.installedBatteryAh ? `${b.unit.installedBatteryAh} Ah` : undefined)),
        g03: line((b) => (b.unit.voltageMainOn != null ? `${b.unit.voltageMainOn} V dc` : undefined)),
        g03c: line((b) => (b.unit.chargingCurrentMa != null ? `${b.unit.chargingCurrentMa} mA` : undefined)),
        g04: line((b) => (b.unit.voltageSupervisoryOff != null ? `${b.unit.voltageSupervisoryOff} V dc` : undefined)),
        g04c: line((b) => (b.unit.currentSupervisoryMa != null ? `${b.unit.currentSupervisoryMa} mA` : undefined)),
        g05: line((b) => (b.unit.voltageAlarmOff != null ? `${b.unit.voltageAlarmOff} V dc` : undefined)),
        g05c: line((b) => (b.unit.currentAlarmA != null ? `${b.unit.currentAlarmA} A` : undefined)),
        g11,
        g12: line((b) => (b.unit.voltageAfterTest != null ? `${b.unit.voltageAfterTest} V dc` : undefined)),
        g18: line((b) => b.unit.manufacturerDateCode),
        g22: line((b) => (b.unit.chargingCurrentA != null ? `${b.unit.chargingCurrentA} A` : undefined))
    };
}
